import { writable, derived, get } from 'svelte/store';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { currentMonth, month, year, prevMonthInfo, nextMonthInfo } from './appData'; // Updated to prevMonthInfo and nextMonthInfo
import type { ExtendedData, FirestoreDataType, Holiday } from '../types/calendar';
import {
  addDataToFirestore,
  updateDataInFirestore,
  deleteItem,
  holidays,
  generateDaysInMonth,
  generatePreviousMonthDays,
  generateNextMonthDays, // Added import for generateNextMonthDays
} from '../util';
import type {
  QuerySnapshot,
  DocumentData,
  QueryDocumentSnapshot,
  FirestoreError,
} from 'firebase/firestore';

export function createCalendarStore(userId: string) {
  // State stores
  const currentMonthData = writable<ExtendedData[]>([]);
  const prevMonthData = writable<ExtendedData[]>([]);
  const selectedDay = writable<ExtendedData | undefined>(undefined);
  // const nextMonthData = writable<ExtendedData[]>([]); // This will be replaced by the derived nextMonthDays
  const nextMonthFirestoreData = writable<ExtendedData[]>([]); // New store for raw Firestore data for the next month

  // Initialize with empty values to ensure reactivity
  currentMonthData.set([]);
  prevMonthData.set([]);
  selectedDay.set(undefined);
  // nextMonthData.set([]); // No longer needed as a separate writable store
  nextMonthFirestoreData.set([]); // Initialize new store

  // Derived stores
  const firstDay = derived([year, currentMonth], ([$year, $currentMonth]) =>
    new Date($year, $currentMonth).getDay(),
  );

  // Firestore query definitions
  function createCurrentMonthQuery($year: number, $month: string, $userId: string) {
    try {
      return query(
        collection(db, 'newMenus', $userId, $year.toString()),
        where('month', '==', $month),
      );
    } catch (error) {
      console.error('Error creating current month query:', error);
      return null;
    }
  }

  function createPrevMonthQuery(
    $prevMonthYear: number, // Use specific year for prev month's query
    $prevMonthLabel: string, // Use specific label for prev month's query
    // $firstDay: number, // No longer needed for fetching the whole month
    $userId: string,
  ) {
    try {
      if (!$prevMonthLabel) {
        return null;
      }
      return query(
        collection(db, 'newMenus', $userId, $prevMonthYear.toString()), // Use $prevMonthYear
        where('month', '==', $prevMonthLabel),
        // orderBy('day', 'asc'), // Fetch all days in ascending order - REMOVED TO AVOID INDEX
        // limitToLast($firstDay || 1), // Removed: Fetch all data for the previous month
      );
    } catch (error) {
      console.error('Error creating previous month query:', error);
      return null;
    }
  }

  function createNextMonthQuery(
    $nextMonthYear: number, // Use specific year for next month's query
    $nextMonthLabel: string, // Use specific label for next month's query
    $userId: string,
  ) {
    try {
      if (!$nextMonthLabel) {
        // console.warn('createNextMonthQuery: $nextMonthLabel is undefined');
        return null;
      }
      return query(
        collection(db, 'newMenus', $userId, $nextMonthYear.toString()),
        where('month', '==', $nextMonthLabel),
        // orderBy('day', 'asc'), // REMOVED TO AVOID INDEX
      );
    } catch (error) {
      console.error('Error creating next month query:', error);
      return null;
    }
  }

  const thisMonthsHolidays = derived([month, year], ([$month, $year]) =>
    holidays.filter(
      (holiday: Holiday) =>
        holiday.month === $month && (holiday.year === $year || holiday.year === 'every'),
    ),
  );

  // Process data
  const daysInMonth = derived(
    [year, currentMonth, month, currentMonthData],
    ([$year, $currentMonth, $month, $currentMonthData]) =>
      generateDaysInMonth($year, $currentMonth, $month, $currentMonthData, holidays),
  );

  const startDays = derived(
    [prevMonthInfo, firstDay, prevMonthData],
    ([$prevMonthInfo, $firstDay, $prevMonthData]) => {
      if (
        !$prevMonthInfo ||
        $prevMonthInfo.year === undefined ||
        $prevMonthInfo.month === undefined ||
        $prevMonthInfo.label === undefined
      ) {
        // console.warn(
        //   'startDays derived: $prevMonthInfo is not fully defined',
        //   $prevMonthInfo,
        // );
        return [];
      }
      return generatePreviousMonthDays(
        $prevMonthInfo.year,
        $prevMonthInfo.month,
        $prevMonthInfo.label,
        $firstDay,
        $prevMonthData,
      );
    },
  );

  const totalDays = derived(
    [firstDay, daysInMonth],
    ([$firstDay, $daysInMonth]) => $firstDay + $daysInMonth.length,
  );

  const nextMonthDays = derived(
    [year, currentMonth, totalDays, nextMonthFirestoreData, nextMonthInfo],
    ([
      $currentDisplayYear,
      $currentDisplayMonthIndex,
      $totalDaysFromPrevAndCurrent,
      $nextMonthFirestore,
      $appDataNextMonthInfo,
    ]) => {
      if (
        !$appDataNextMonthInfo ||
        $appDataNextMonthInfo.year === undefined ||
        $appDataNextMonthInfo.month === undefined
      ) {
        // console.warn(
        //   'nextMonthDays derived: $appDataNextMonthInfo is not fully defined',
        //   $appDataNextMonthInfo,
        // );
        return [];
      }
      return generateNextMonthDays(
        $currentDisplayYear, // Year of the month currently being displayed (e.g., June 2025)
        $currentDisplayMonthIndex, // Month index of the month currently being displayed (e.g., 5 for June)
        $totalDaysFromPrevAndCurrent,
        $nextMonthFirestore, // Data fetched for the actual next month (e.g., July 2025)
        $appDataNextMonthInfo.year, // Actual year of the next month (e.g., 2025 for July)
        $appDataNextMonthInfo.month, // Actual month index of the next month (e.g., 6 for July)
      );
    },
  );

  // Subscribe to Firestore data
  let currentMonthUnsubscribe: (() => void) | undefined;
  let prevMonthUnsubscribe: (() => void) | undefined;
  let nextMonthUnsubscribe: (() => void) | undefined; // For next month's data

  // Handle for all subscriptions
  let unsubscribers: (() => void)[] = [];

  // Set up subscriptions
  function setupSubscriptions() {
    // console.log('Setting up calendar store subscriptions');

    try {
      // Get current values for queries
      let currentYearValue = 0;
      let currentMonthValue = ''; // Month label, e.g., "June"
      let currentFirstDayValue = 0;
      let currentPrevMonthInfo: any = null;
      let currentAppDataNextMonthInfo: any = null;

      // Initialize values by subscribing and immediately unsubscribing
      const yearInitUnsub = year.subscribe(value => {
        currentYearValue = value;
      })();
      const monthInitUnsub = month.subscribe(value => {
        currentMonthValue = value;
      })(); // This is the label
      const firstDayInitUnsub = firstDay.subscribe(value => {
        currentFirstDayValue = value;
      })();
      const prevMonthInfoInitUnsub = prevMonthInfo.subscribe(value => {
        currentPrevMonthInfo = value;
      })(); // Updated
      const nextMonthInfoInitUnsub = nextMonthInfo.subscribe(value => {
        currentAppDataNextMonthInfo = value;
      })(); // Updated

      // Subscribe to stores and refresh queries when they change
      const yearUnsub = year.subscribe(value => {
        // console.log(`Year changed to: ${value}`);
        refreshQueries();
      });
      unsubscribers.push(yearUnsub);

      const monthUnsub = month.subscribe(value => {
        // console.log(`Month changed to: ${value}`);
        refreshQueries();
      });
      unsubscribers.push(monthUnsub);

      // No need to subscribe to firstDay or prevMonth for refreshQueries,
      // as they depend on year/month which already trigger refreshQueries.
      // Subscribing to appDataNextMonth is important if its calculation changes independently
      const appDataNextMonthUnsubStore = nextMonthInfo.subscribe(value => {
        // Updated
        // console.log(`Next month (appData) changed to: ${value?.label}`);
        refreshQueries();
      });
      unsubscribers.push(appDataNextMonthUnsubStore);

      // Added subscription for prevMonthInfo to trigger refreshQueries
      const prevMonthInfoUnsubStore = prevMonthInfo.subscribe(value => {
        // New
        // console.log(`Prev month (appData) changed to: ${value?.label}`);
        refreshQueries();
      });
      unsubscribers.push(prevMonthInfoUnsubStore); // New

      // Create our queries directly instead of using derived stores
      const currentMonthQueryObj = createCurrentMonthQuery(
        currentYearValue,
        currentMonthValue, // Use month label
        userId,
      );

      const prevMonthQueryObj = currentPrevMonthInfo
        ? createPrevMonthQuery(
            currentPrevMonthInfo.year, // Use prev month's actual year
            currentPrevMonthInfo.label, // Pass the label directly
            // currentFirstDayValue, // No longer needed
            userId,
          )
        : null;

      const nextMonthQueryObj = currentAppDataNextMonthInfo
        ? createNextMonthQuery(
            currentAppDataNextMonthInfo.year,
            currentAppDataNextMonthInfo.label,
            userId,
          )
        : null;

      // Set up current month subscription if we have a valid query
      if (currentMonthQueryObj) {
        if (currentMonthUnsubscribe) {
          currentMonthUnsubscribe();
          currentMonthUnsubscribe = undefined;
        }
        try {
          currentMonthUnsubscribe = onSnapshot(
            currentMonthQueryObj,
            (snapshot: QuerySnapshot<DocumentData>) => {
              try {
                const docs = snapshot.docs.map(
                  (doc: QueryDocumentSnapshot<DocumentData>) => ({
                    ...doc.data(),
                    id: doc.id,
                  }),
                );
                // console.log(`Received ${docs.length} current month documents`);
                currentMonthData.set(docs as ExtendedData[]);
              } catch (error) {
                console.error('Error processing current month snapshot:', error);
                currentMonthData.set([]);
              }
            },
            (error: FirestoreError) => {
              console.error('Error in current month subscription:', error);
              currentMonthData.set([]);
            },
          );
          if (currentMonthUnsubscribe) {
            unsubscribers.push(currentMonthUnsubscribe);
          }
        } catch (error) {
          console.error('Error setting up current month snapshot:', error);
          currentMonthData.set([]);
        }
      }

      // Set up previous month subscription if we have a valid query
      if (prevMonthQueryObj) {
        if (prevMonthUnsubscribe) {
          prevMonthUnsubscribe();
          prevMonthUnsubscribe = undefined;
        }
        try {
          prevMonthUnsubscribe = onSnapshot(
            prevMonthQueryObj,
            (snapshot: QuerySnapshot<DocumentData>) => {
              try {
                const docs = snapshot.docs
                  .map(
                    (doc: QueryDocumentSnapshot<DocumentData>) =>
                      ({
                        ...doc.data(),
                        id: doc.id,
                      } as ExtendedData),
                  )
                  .sort((a, b) => a.day - b.day); // Sort client-side
                // console.log(`Received ${docs.length} previous month documents`);
                prevMonthData.set(docs);
              } catch (error) {
                console.error('Error processing previous month snapshot:', error);
                prevMonthData.set([]);
              }
            },
            (error: FirestoreError) => {
              console.error('Error in previous month subscription:', error);
              prevMonthData.set([]);
            },
          );
          if (prevMonthUnsubscribe) {
            unsubscribers.push(prevMonthUnsubscribe);
          }
        } catch (error) {
          console.error('Error setting up previous month snapshot:', error);
          prevMonthData.set([]);
        }
      }

      // Set up next month subscription if we have a valid query
      if (nextMonthQueryObj) {
        if (nextMonthUnsubscribe) {
          nextMonthUnsubscribe();
          nextMonthUnsubscribe = undefined;
        }
        try {
          nextMonthUnsubscribe = onSnapshot(
            nextMonthQueryObj,
            (snapshot: QuerySnapshot<DocumentData>) => {
              try {
                const docs = snapshot.docs
                  .map(
                    (doc: QueryDocumentSnapshot<DocumentData>) =>
                      ({
                        ...doc.data(),
                        id: doc.id,
                      } as ExtendedData),
                  )
                  .sort((a, b) => a.day - b.day); // Sort client-side
                // console.log(`Received ${docs.length} next month documents`);
                nextMonthFirestoreData.set(docs);
              } catch (error) {
                console.error('Error processing next month snapshot:', error);
                nextMonthFirestoreData.set([]);
              }
            },
            (error: FirestoreError) => {
              console.error('Error in next month subscription:', error);
              nextMonthFirestoreData.set([]);
            },
          );
          if (nextMonthUnsubscribe) {
            unsubscribers.push(nextMonthUnsubscribe);
          }
        } catch (error) {
          console.error('Error setting up next month snapshot:', error);
          nextMonthFirestoreData.set([]);
        }
      }

      // Return cleanup function
      return () => {
        // console.log('Cleaning up all calendar store subscriptions');
        unsubscribers.forEach(unsub => {
          try {
            unsub();
          } catch (e) {
            console.error('Error during unsubscribe:', e);
          }
        });
        unsubscribers = [];
      };
    } catch (error) {
      console.error('Error in setupSubscriptions:', error);
      return () => {
        // No-op cleanup
      };
    }
  }

  // Function to refresh Firestore queries when month/year change
  function refreshQueries() {
    // console.log('Refreshing Firestore queries for month/year change');

    try {
      // Get current values for queries
      let currentYearValue = 0;
      let currentMonthLabelValue = ''; // Month label, e.g., "June"
      let currentFirstDayValue = 0;
      let currentPrevMonthInfo: any = null;
      let currentAppDataNextMonthInfo: any = null;

      const yearUnsub = year.subscribe(value => {
        currentYearValue = value;
      })();
      const monthUnsub = month.subscribe(value => {
        currentMonthLabelValue = value;
      })();
      const firstDayUnsub = firstDay.subscribe(value => {
        currentFirstDayValue = value;
      })();
      const prevMonthInfoUnsubStore = prevMonthInfo.subscribe(value => {
        currentPrevMonthInfo = value;
      })(); // Updated
      const nextMonthInfoUnsubStore = nextMonthInfo.subscribe(value => {
        currentAppDataNextMonthInfo = value;
      })(); //Updated

      // console.log(`Creating queries for ${currentMonthLabelValue} ${currentYearValue}`);

      // Create queries with current values
      const currentMonthQueryObj = createCurrentMonthQuery(
        currentYearValue,
        currentMonthLabelValue,
        userId,
      );

      const prevMonthQueryObj = currentPrevMonthInfo
        ? createPrevMonthQuery(
            currentPrevMonthInfo.year,
            currentPrevMonthInfo.label, // Pass the label
            // currentFirstDayValue, // No longer needed
            userId,
          )
        : null;

      const nextMonthQueryObj = currentAppDataNextMonthInfo
        ? createNextMonthQuery(
            currentAppDataNextMonthInfo.year,
            currentAppDataNextMonthInfo.label,
            userId,
          )
        : null;

      // Clean up existing subscriptions
      if (currentMonthUnsubscribe) {
        currentMonthUnsubscribe();
        currentMonthUnsubscribe = undefined;
      }
      if (prevMonthUnsubscribe) {
        prevMonthUnsubscribe();
        prevMonthUnsubscribe = undefined;
      }
      if (nextMonthUnsubscribe) {
        nextMonthUnsubscribe();
        nextMonthUnsubscribe = undefined;
      }

      // Set up current month subscription
      if (currentMonthQueryObj) {
        currentMonthUnsubscribe = onSnapshot(
          currentMonthQueryObj,
          (snapshot: QuerySnapshot<DocumentData>) => {
            try {
              const docs = snapshot.docs.map(
                (doc: QueryDocumentSnapshot<DocumentData>) => ({
                  ...doc.data(),
                  id: doc.id,
                }),
              );

              currentMonthData.set(docs as ExtendedData[]);
            } catch (error) {
              console.error('Error processing current month snapshot:', error);
              currentMonthData.set([]);
            }
          },
          (error: FirestoreError) => {
            console.error('Error in current month subscription:', error);
            currentMonthData.set([]);
          },
        );
      }

      // Set up previous month subscription
      if (prevMonthQueryObj) {
        prevMonthUnsubscribe = onSnapshot(
          prevMonthQueryObj,
          (snapshot: QuerySnapshot<DocumentData>) => {
            try {
              const docs = snapshot.docs
                .map(
                  (doc: QueryDocumentSnapshot<DocumentData>) =>
                    ({
                      ...doc.data(),
                      id: doc.id,
                    } as ExtendedData),
                )
                .sort((a, b) => a.day - b.day); // Sort client-side
              prevMonthData.set(docs);
            } catch (error) {
              console.error('Error processing previous month snapshot:', error);
              prevMonthData.set([]);
            }
          },
          (error: FirestoreError) => {
            console.error('Error in previous month subscription:', error);
            prevMonthData.set([]);
          },
        );
      }

      // Set up next month subscription
      if (nextMonthQueryObj) {
        nextMonthUnsubscribe = onSnapshot(
          nextMonthQueryObj,
          (snapshot: QuerySnapshot<DocumentData>) => {
            try {
              const docs = snapshot.docs
                .map(
                  (doc: QueryDocumentSnapshot<DocumentData>) =>
                    ({
                      ...doc.data(),
                      id: doc.id,
                    } as ExtendedData),
                )
                .sort((a, b) => a.day - b.day); // Sort client-side

              nextMonthFirestoreData.set(docs);
            } catch (error) {
              console.error('Error processing next month snapshot:', error);
              nextMonthFirestoreData.set([]);
            }
          },
          (error: FirestoreError) => {
            console.error('Error in next month subscription:', error);
            nextMonthFirestoreData.set([]);
          },
        );
      }
    } catch (error) {
      console.error('Error refreshing queries:', error);
    }
  }

  // Data operations
  function saveData(data: ExtendedData) {
    addDataToFirestore(data, userId);
  }

  function updateData(data: ExtendedData) {
    updateDataInFirestore(data, userId);
  }

  function removeItem(id: string, yearValue: string) {
    deleteItem(userId, yearValue, id);
  }

  function setUpdatedValue(detail: ExtendedData) {
    const currentMonthValue = get(currentMonthData);
    const monthValue = get(month);
    const yearValue = get(year);

    const [existingEntry] = currentMonthValue.filter(d => d.id === detail.id);

    if (existingEntry !== undefined) {
      if (existingEntry?.html === detail.html) return;
      updateData({ ...detail, month: monthValue, year: yearValue });
      return;
    }
    saveData({ ...detail, month: monthValue, year: yearValue });
  }

  function selectDay(index: number) {
    selectedDay.set(get(daysInMonth)[index]);
  }

  function resetSelectedDay() {
    selectedDay.set(undefined);
  }

  // Cleanup function
  function cleanup() {
    if (currentMonthUnsubscribe) {
      currentMonthUnsubscribe();
      currentMonthUnsubscribe = undefined;
    }
    if (prevMonthUnsubscribe) {
      prevMonthUnsubscribe();
      prevMonthUnsubscribe = undefined;
    }
    if (nextMonthUnsubscribe) {
      // console.log('Cleaning up next month subscription');
      try {
        nextMonthUnsubscribe();
      } catch (e) {
        console.error('Error cleaning up next month subscription:', e);
      }
      nextMonthUnsubscribe = undefined;
    }

    if (storeUnsubscribe) {
      // console.log('Cleaning up store subscriptions');
      try {
        storeUnsubscribe();
      } catch (e) {
        console.error('Error cleaning up store subscriptions:', e);
      }
    }
  }

  // Initialize subscriptions
  // console.log('Setting up store subscriptions for user:', userId);
  const storeUnsubscribe = setupSubscriptions();

  return {
    // Stores
    currentMonthData: { subscribe: currentMonthData.subscribe },
    prevMonthData: { subscribe: prevMonthData.subscribe },
    daysInMonth: { subscribe: daysInMonth.subscribe },
    startDays: { subscribe: startDays.subscribe },
    nextMonthDays: { subscribe: nextMonthDays.subscribe },
    thisMonthsHolidays: { subscribe: thisMonthsHolidays.subscribe },
    totalDays: { subscribe: totalDays.subscribe },
    selectedDay: {
      subscribe: selectedDay.subscribe,
      set: selectedDay.set,
    },

    // Actions
    setUpdatedValue,
    removeItem,
    selectDay,
    resetSelectedDay,
    cleanup,
    refreshQueries,
  };
}
